import { Box, Button, Flex, Heading, Link, Separator, SimpleGrid, Spinner, Switch, Text } from "@chakra-ui/react"
import { useState, useEffect, useRef, useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { LuDownload, LuFileX } from "react-icons/lu";
import { useChatHistory } from "../chat-history";
import { ColorPickRow } from "./color-pick";
import { useAccount } from "./account-context";
import config from '../../config.json';
import { useAuth } from "./auth-context";
import { ThemeSelect } from "./theme-select";
import { updateUserAutoDelete } from "../../api";
import { DialogRoot, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogCloseTrigger } from "./dialog";

interface SettingsPageProps {
    fontFamily: string,
}

const slugify = (str: string) =>
    str.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').trim();

interface MarkdownViewerDialogProps {
    title: string;
    src: string;
    fontFamily: string;
}

const MarkdownViewerDialog = ({ title, src, fontFamily }: MarkdownViewerDialogProps) => {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [activeId, setActiveId] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setError(false);
        fetch(src)
            .then(r => { if (!r.ok) throw new Error(); return r.text(); })
            .then(text => { setContent(text); setLoading(false); })
            .catch(() => { setError(true); setLoading(false); });
    }, [open, src]);

    // Parse headings from raw markdown for the sidebar nav
    const navHeadings = useMemo(() => {
        return (content.match(/^#{1,4} .+$/gm) ?? []).map(line => {
            const level = line.match(/^(#+)/)?.[1].length ?? 1;
            const text = line.replace(/^#+\s+/, '').replace(/\*\*/g, '');
            return { level, text, id: slugify(text) };
        });
    }, [content]);

    // Track which heading is currently in view
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !content) return;
        const update = () => {
            const headings = Array.from(el.querySelectorAll('h1[id],h2[id],h3[id],h4[id]'));
            const containerTop = el.getBoundingClientRect().top;
            let current = headings[0]?.id ?? '';
            for (const h of headings) {
                if (h.getBoundingClientRect().top - containerTop <= 48) current = h.id;
            }
            setActiveId(current);
        };
        el.addEventListener('scroll', update, { passive: true });
        update();
        return () => el.removeEventListener('scroll', update);
    }, [content]);

    const scrollTo = (id: string) =>
        scrollRef.current?.querySelector(`[id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const mdComponents = {
        h1: ({ children, node: _node }: any) => { const id = slugify(Array.isArray(children) ? children.join('') : String(children ?? '')); return <Text as="h1" id={id} fontSize="2xl" fontWeight="bold" mt={6} mb={3} pb={2} borderBottomWidth="1px">{children}</Text>; },
        h2: ({ children, node: _node }: any) => { const id = slugify(Array.isArray(children) ? children.join('') : String(children ?? '')); return <Text as="h2" id={id} fontSize="xl" fontWeight="bold" mt={5} mb={2} pb={2} borderBottomWidth="1px">{children}</Text>; },
        h3: ({ children, node: _node }: any) => { const id = slugify(Array.isArray(children) ? children.join('') : String(children ?? '')); return <Text as="h3" id={id} fontSize="lg" fontWeight="semibold" mt={4} mb={2}>{children}</Text>; },
        h4: ({ children, node: _node }: any) => { const id = slugify(Array.isArray(children) ? children.join('') : String(children ?? '')); return <Text as="h4" id={id} fontSize="md" fontWeight="semibold" mt={3} mb={1}>{children}</Text>; },
        p: (props: any) => <Text mb={3} lineHeight="1.7" {...props} />,
        ul: (props: any) => <Box as="ul" ml={6} mb={3} style={{ listStyleType: 'disc' }} {...props} />,
        ol: (props: any) => <Box as="ol" ml={6} mb={3} style={{ listStyleType: 'decimal' }} {...props} />,
        li: (props: any) => <Box as="li" mb={1} lineHeight="1.7" {...props} />,
        a: ({ href, children, node }: any) => {
            if (href?.startsWith('#')) {
                return (
                    <Box as="a" color="blue.500" textDecoration="underline" cursor="pointer"
                        onClick={(e: React.MouseEvent) => { e.preventDefault(); scrollTo(href.slice(1)); }}>
                        {children}
                    </Box>
                );
            }
            return <Link color="blue.500" textDecoration="underline" href={href} target="_blank" rel="noopener noreferrer">{children}</Link>;
        },
        strong: (props: any) => <Box as="strong" fontWeight="bold" display="inline" {...props} />,
        em: (props: any) => <Box as="em" fontStyle="italic" display="inline" {...props} />,
        code: (props: any) => <Box as="code" bg="var(--chakra-colors-bg-muted)" px={1} py="1px" borderRadius="sm" fontSize="0.875em" fontFamily="monospace" {...props} />,
        pre: (props: any) => <Box as="pre" bg="var(--chakra-colors-bg-muted)" borderWidth="1px" borderRadius="md" p={4} overflowX="auto" mb={4} fontSize="sm" fontFamily="monospace" {...props} />,
        blockquote: (props: any) => <Box as="blockquote" borderLeftWidth="4px" borderLeftColor="var(--chakra-colors-border)" pl={4} ml={0} mb={3} color="var(--chakra-colors-fg-muted)" fontStyle="italic" {...props} />,
        hr: () => <Separator my={5} />,
        table: (props: any) => <Box as="table" w="100%" mb={4} display="block" overflowX="auto" style={{ borderCollapse: 'collapse' }} {...props} />,
        thead: (props: any) => <Box as="thead" bg="var(--chakra-colors-bg-muted)" {...props} />,
        tr: (props: any) => <Box as="tr" borderBottomWidth="1px" {...props} />,
        th: (props: any) => <Box as="th" px={3} py={2} textAlign="left" fontWeight="semibold" fontSize="sm" borderWidth="1px" style={{ whiteSpace: 'nowrap' }} {...props} />,
        td: (props: any) => <Box as="td" px={3} py={2} fontSize="sm" borderWidth="1px" {...props} />,
    };

    return (
        <>
            <Link pt="0.25rem" display="block" colorPalette="blue" variant="underline" fontSize="xs"
                cursor="pointer" onClick={() => setOpen(true)}>
                {title}
            </Link>
            <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} size="full" placement="center" children={
                <DialogContent maxH="90vh" display="flex" flexDir="column">
                    <DialogHeader borderBottomWidth="1px" pb={3}>
                        <DialogTitle fontFamily={fontFamily} fontSize="lg">{title}</DialogTitle>
                    </DialogHeader>
                    <DialogBody p={0} display="flex" flex="1" minH={0} overflow="hidden" fontFamily={fontFamily}>
                        {/* Sidebar nav — only shown when headings are available */}
                        {navHeadings.length > 0 && !loading && !error && (
                            <Box w="185px" flexShrink={0} borderRightWidth="1px" overflowY="auto" py={5} px={3}>
                                <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} px={2}
                                    textTransform="uppercase" letterSpacing="wider">
                                    Contents
                                </Text>
                                {navHeadings.map((h, i) => (
                                    <Box
                                        key={i}
                                        ml={Math.max(0, h.level - 2) * 3}
                                        pl={2}
                                        py="3px"
                                        borderLeftWidth="2px"
                                        borderLeftColor={activeId === h.id ? 'colorPalette.fg' : 'transparent'}
                                        color={activeId === h.id ? 'fg' : 'fg.muted'}
                                        fontWeight={activeId === h.id ? 'medium' : 'normal'}
                                        fontSize="xs"
                                        lineHeight="1.4"
                                        cursor="pointer"
                                        _hover={{ color: 'fg' }}
                                        transition="color 0.15s"
                                        onClick={() => scrollTo(h.id)}
                                        tabIndex={0}
                                        role="button"
                                        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollTo(h.id); } }}
                                    >
                                        {h.text}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {/* Main content */}
                        <Box ref={scrollRef} flex={1} overflowY="auto" py={6} px={8}>
                            {loading && (
                                <Flex justify="center" align="center" minH="200px">
                                    <Spinner size="lg" />
                                </Flex>
                            )}
                            {error && <Text color="red.500">Failed to load content. Please try again.</Text>}
                            {!loading && !error && content && (
                                <Box fontSize="sm" lineHeight="1.7">
                                    <Markdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} components={mdComponents}>{content}</Markdown>
                                </Box>
                            )}
                        </Box>
                    </DialogBody>
                    <DialogCloseTrigger />
                </DialogContent>
            } />
        </>
    );
};



export const GeneralPage = (_props: SettingsPageProps) => {

    return (
        <Box w={'100%'} fontFamily={_props.fontFamily}>
            <Heading fontFamily={'inherit'} p={0} pb={'0.5rem'} size={'md'}>General Settings</Heading>
            <Flex direction="column" gap={4}>
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Language</Text>
                    <Text color="gray.500" fontSize="xs">Choose your preferred language for the interface (Coming soon).</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Theme Preference</Text>
                    <Text color="gray.500" fontSize="xs">Toggle between Light, Dark, and System theme.</Text>
                    <ThemeSelect fontFamily={_props.fontFamily} />
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Compact Mode</Text>
                    <Text color="gray.500" fontSize="xs">Enable a denser layout for chat messages (Coming soon).</Text>
                </Box>
            </Flex>
        </Box>
    )
}
// export const InterfacePage = (_props: SettingsPageProps) => {
//     return (
//         <Box fontFamily={_props.fontFamily} w={'100%'}>
//             <Heading fontFamily={'inherit'} pb={'0.5rem'} size={'md'}>UI</Heading>
//         </Box>
//     )
// }
// export const PersonalizationPage = (_props: SettingsPageProps) => {
//     return (
//         <Box fontFamily={_props.fontFamily} w={'100%'}>
//             {/* <Heading fontFamily={'inherit'} pb={'0.5rem'} size={'md'}>Personalization</Heading> */}
//             <Heading fontFamily={'inherit'} p={0} pb={'0.5rem'} size={'md'}>Color Profile / Theme</Heading>
//             <SimpleGrid columns={3} >
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text pr={'8px'} fontWeight={'bold'}>50</Text>
//                     <ColorPickRow colorKey="50" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text pr={'3px'} fontWeight={'bold'}>100</Text>
//                     <ColorPickRow colorKey="100" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>200</Text>
//                     <ColorPickRow colorKey="200" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>300</Text>
//                     <ColorPickRow colorKey="300" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>400 </Text>
//                     <ColorPickRow colorKey="400" disabled />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>500</Text>
//                     <ColorPickRow colorKey="500" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>600</Text>
//                     <ColorPickRow colorKey="600" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>700</Text>
//                     <ColorPickRow colorKey="700" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>800</Text>
//                     <ColorPickRow colorKey="800" />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>900</Text>
//                     <ColorPickRow colorKey="900" disabled />
//                 </Flex>
//                 <Flex gap={2} alignItems={'center'} mb={4}>
//                     <Text fontWeight={'bold'}>950 </Text>
//                     <ColorPickRow colorKey="950" disabled />
//                 </Flex>
//             </SimpleGrid>
//         </Box>
//     )
// }

// export const AudioPage = (_props: SettingsPageProps) => {
//     return (
//         <Box>Audio</Box>
//     )
// }
export const ChatsPage = (_props: SettingsPageProps) => {
    const { user, updateUser } = useAuth();
    const {
        deleteAllSessions,
        downloadHistory,
    } = useChatHistory();

    const autoDelete = user?.autoDelete || false;

    const handleAutoDeleteChange = async (checked: boolean) => {
        if (!user?.nyssoguid) return;
        try {
            await updateUserAutoDelete(user.nyssoguid, checked);
            updateUser({ autoDelete: checked });
        } catch (error) {
            console.error("Failed to update auto-delete preference:", error);
        }
    };

    const handleDeleteAll = () => {
        if (window.confirm("Are you sure you want to delete all conversations? This action cannot be undone.")) {
            deleteAllSessions();
        }
    };

    return (
        <Box w={'100%'} fontFamily={_props.fontFamily}>
            <Heading fontFamily={'inherit'} p={0} pb={'0.5rem'} size={'md'}>Chat Settings</Heading>
            <Flex direction="column" gap={4}>
                <Box>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontWeight="semibold" fontSize="sm">Auto-delete History</Text>
                            <Text color="gray.500" fontSize="xs">Automatically delete conversations older than 30 days.</Text>
                        </Box>
                        <Switch.Root checked={autoDelete} onCheckedChange={(e) => handleAutoDeleteChange(e.checked)}>
                            <Switch.HiddenInput />
                            <Switch.Control />
                        </Switch.Root>
                    </Flex>
                </Box>
                <Separator />
                {/* <Box>
                    <Text fontWeight="semibold" fontSize="sm">Feedback Settings</Text>
                    <Text color="gray.500" fontSize="xs">Enable or disable feedback options (Coming soon)</Text>
                </Box>
                <Separator /> */}
                <Box>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontWeight="semibold" fontSize="sm">Export History</Text>
                            <Text color="gray.500" fontSize="xs">Download your entire chat history as a JSON file.</Text>
                        </Box>
                        <Button onClick={() => { downloadHistory() }} variant="outline" size="sm"><LuDownload /> Export</Button>
                    </Flex>
                </Box>
                <Separator />
                <Box>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontWeight="semibold" fontSize="sm">Delete All History</Text>
                            <Text color="gray.500" fontSize="xs">Permanently remove all conversations from your account.</Text>
                        </Box>
                        <Button onClick={handleDeleteAll} colorPalette={'red'} variant="subtle" size="sm"><LuFileX /> Delete All</Button>
                    </Flex>
                </Box>
                <Separator />
                {/* <Box>
                    <Text fontWeight="semibold" fontSize="sm">Privacy & Data</Text>
                    <Text color="gray.500" fontSize="xs">Your conversations are stored securely and are not used for model training without your explicit consent.</Text>
                </Box> */}
            </Flex>
        </Box>
    )
}

export const AccountPage = (_props: SettingsPageProps) => {
    const { accountType } = useAccount();
    const { user } = useAuth();
    
    const name = user ? user.name : "Unknown User";
    const email = user ? user.email : "N/A";
    const organization = user ? user.OU : "N/A";

    return (
        <Box w={'100%'} fontFamily={_props.fontFamily}>
            <Heading fontFamily={'inherit'} p={0} pb={'0.5rem'} size={'md'}>Account Settings</Heading>
            <Flex direction="column" gap={4}>
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Profile Information</Text>
                    <Text color="gray.500" fontSize="xs">{name}</Text>
                    <Text color="gray.500" fontSize="xs">{email}</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Organization</Text>
                    <Text color="gray.500" fontSize="xs">{organization}</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Account Type</Text>
                    <Text color="gray.500" fontSize="xs">{accountType === 'admin' ? 'Administrator' : 'Standard User'}</Text>
                </Box>
                <Separator />
                {/* <Box>
                    <Text fontWeight="bold" fontSize="sm">Security</Text>
                    <Text color="gray.500" fontSize="xs">Manage passwords and multi-factor authentication (Coming soon).</Text>
                </Box> */}
                {/* <Separator />
                <Box>
                    <Text fontWeight="bold" fontSize="sm">Active Sessions</Text>
                    <Text color="gray.500" fontSize="xs">View and manage where you're currently logged in (Coming soon).</Text>
                </Box> */}
            </Flex>
        </Box>
    )
}

export const AboutPage = (_props: SettingsPageProps) => {
    return (
        <Box w={'100%'} fontFamily={_props.fontFamily}>
            <Heading fontFamily={'inherit'} p={0} pb={'0.5rem'} size={'md'}>About & Support</Heading>
            <Flex direction="column" gap={4}>
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Software Information</Text>
                    <Text color="gray.500" fontSize="xs">Version: {config.version}</Text>
                    <Text color="gray.500" fontSize="xs">Build: {config.build}</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Resources</Text>
                    <MarkdownViewerDialog title="Patch Notes" src="/patch-notes.md" fontFamily={_props.fontFamily} />
                    <Text color="gray.500" fontSize="xs" mt={1}>Check out what's new in the latest update.</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Legal & Privacy</Text>
                    <Text color="gray.500" fontSize="xs">Terms of Service and Privacy Policy (Coming soon).</Text>
                </Box>
                <Separator />
                <Box>
                    <Text fontWeight="semibold" fontSize="sm">Support</Text>
                    <MarkdownViewerDialog title="User Guide" src="/user-guide.md" fontFamily={_props.fontFamily} />
                    <Text color="gray.500" fontSize="xs" mt={1}>Read the end-user guide for help getting started.</Text>
                    <Text color="gray.500" fontSize="xs" mt={2}>Need additional help? Contact our support team (Coming soon).</Text>
                </Box>
            </Flex>
        </Box>
    )
}
